'use client'
import { create } from 'zustand'
import * as React from 'react'
import { useClerk, useUser } from '@clerk/nextjs'
import _isEqual from 'lodash.isequal'
import { loginRoute } from '~/constants/routing'
import { PublicUser } from '~/app/api/users/public-user-schema'
import { getDefaultAvatarSrc } from '~/app/api/default-avatar/util'
import { useRouter } from 'next/navigation'
import throttle from 'lodash.throttle'
import { z } from 'zod'
import { User, userSchema } from '../api/users/user-schema'

export const defaultPreferences: User['preferences'] = {
  emailNotifications: true,
}

type UserStore = {
  optimisticUser: PublicUser | null
  isLoading: boolean
  isSyncing: boolean
  isSigningOut: boolean
  joined: string | null
  lastAnnouncementReceived: string | null
  followingUserId: null | string
  updateUserPreferences: (newPreferences: Partial<User['preferences']>) => void
}
// In dev/basehub/apps/www/src/app/_store/user-store.ts

export const useUserStore = create<UserStore>()((set) => ({
  optimisticUser: null,
  isLoading: true,
  isSyncing: true,
  isSigningOut: false,
  joined: null,
  lastAnnouncementReceived: null,
  followingUserId: null,
  updateUserPreferences: (newPreferences: Partial<User['preferences']>) =>
    set((state) => ({
      optimisticUser: state.optimisticUser
        ? {
            ...state.optimisticUser,
            preferences: {
              ...defaultPreferences,
              ...state.optimisticUser.preferences,
              ...newPreferences,
            },
          }
        : null,
    })),
}))

export const UserStoreSetUp = () => {
  // source of truth
  const { user } = useUser()

  const userData = React.useMemo(() => {
    if (user === undefined) return undefined
    const showReleaseNoteParsed = z
      .boolean()
      .safeParse(user?.publicMetadata?.showReleaseNote)
    const userPreferences = userSchema.shape.preferences
      .nullish()
      .default({
        emailNotifications: true,
      })
      .parse(user?.publicMetadata.preferences)

    return user
      ? {
          id: user.id,
          email:
            user.primaryEmailAddress?.verification.status === 'verified'
              ? user.primaryEmailAddress.emailAddress
              : null,
          firstName: user.firstName,
          lastName: user.lastName,
          bio: (user.publicMetadata.bio as string | undefined) ?? null,
          lastAnnouncementReceived:
            (user.publicMetadata.lastAnnouncementReceived as
              | string
              | undefined) ?? null,
          lastChangelogSeen:
            (user.publicMetadata.lastChangelogSeen as string | undefined) ??
            null,
          username: user.username,
          imageUrl: user.hasImage
            ? user.imageUrl
            : getDefaultAvatarSrc(user.id),
          joined: user.createdAt?.toISOString() ?? null,
          showReleaseNote: showReleaseNoteParsed.success
            ? showReleaseNoteParsed.data
            : true,
          preferences:
            userPreferences ??
            ({
              emailNotifications: true,
            } satisfies User['preferences']),
        }
      : null
  }, [user])

  // quickly sync with localStorage
  React.useEffect(() => {
    const lsData = window.localStorage.getItem('public-user')
    let optimisticUser
    if (lsData) {
      const parsed = PublicUser.nullable().safeParse(JSON.parse(lsData))
      if (!parsed.success) return
      optimisticUser = parsed.data
    }
    useUserStore.setState({
      optimisticUser: optimisticUser ?? null,
      isLoading: false,
    })
  }, [])

  React.useEffect(() => {
    const resetFollowStateOnInput = throttle(() => {
      useUserStore.setState({ followingUserId: null })
    }, 1500)

    window.addEventListener('input', resetFollowStateOnInput)
    return () => window.removeEventListener('input', resetFollowStateOnInput)
  }, [])

  React.useEffect(() => {
    if (userData === undefined) return

    const current = useUserStore.getState().optimisticUser
    const shouldUpdateUser = _isEqual(userData, current) === false

    useUserStore.setState({
      isLoading: false,
      isSyncing: false,
      ...(shouldUpdateUser ? { optimisticUser: userData } : {}),
    })
    // also save to localStorage
    window.localStorage.setItem('public-user', JSON.stringify(userData))
  }, [userData])

  return null
}

export const useSignOut = () => {
  const { signOut } = useClerk()
  const router = useRouter()

  return React.useCallback(
    async (next?: string) => {
      useUserStore.setState({ isSigningOut: true })
      return await signOut().then(() => {
        useUserStore.setState({ isSigningOut: false, optimisticUser: null })
        router.push(next ?? loginRoute)
      })
    },
    [router, signOut]
  )
}
